import React from 'react';
import { useRouter } from 'next/router';
import { createColumnHelper } from '@tanstack/react-table';
import { Card, DotIcon, Table } from '@avila-tek/ui/src';
import { Hit } from '@algolia/client-search';
import { Category, Company, PageInfo, Pagination, Product } from '../../models';
import { ActiveDotCell } from '../shared';
import { useAlgoliaSearch, useUser } from '../../hooks';
import { AlgoliaHitToObject } from '../../lib';
import { roles } from '../../static';

const columnHelper = createColumnHelper<Product>();

const columns = [
  columnHelper.accessor('title', {
    header: 'Título',
  }),

  columnHelper.accessor('category', {
    header: 'Categoría',
    cell: (info) => (info?.getValue() as Category).name,
  }),

  columnHelper.accessor('active', {
    header: 'Estatus',
    cell: (info) => <ActiveDotCell active={info?.getValue()} />,
  }),
];

interface ProductTableProps {
  data: Pagination<Product>;
  pagination: Partial<PageInfo>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
}

function ProductTable({ data, pagination, setPagination }: ProductTableProps) {
  const router = useRouter();
  const [user] = useUser();
  const [searchText, setSearchText] = React.useState<string | number>('');
  const [productsData, setProductsData] = React.useState(data.items);
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const { fetchData } = useAlgoliaSearch<Product>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: [
      'category',
      'category.name',
      'company',
      'company.name',
      'title',
      'description',
    ],
    index: 'products',
    filters:
      user?.userType === roles?.ally
        ? `company._id:${(user.company as Company)._id}`
        : '',
  });

  React.useEffect(() => {
    async function syncData() {
      if (searchText) {
        const { pageInfo, items } = await fetchData({
          searchText: searchText.toString(),
          newPage: pageInfoAlgolia.currentPage,
        });
        setProductsData(AlgoliaHitToObject<Product>(items as Hit<Product>[]));
        setPageInfoAlgolia(pageInfo);
      } else {
        setProductsData(data.items);
      }
    }
    syncData();
  }, [pageInfoAlgolia?.currentPage, searchText, pagination]);

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    router.push(`/app/products/${id}`);
  };

  const onClickAddButton = () => router.push('/app/products/create');

  const handleGoToPage = (value: number) => {
    if (searchText) {
      setPageInfoAlgolia({ ...pageInfoAlgolia, currentPage: value - 1 });
    } else {
      setPagination({ ...pagination, currentPage: value });
    }
  };

  return (
    <Card className="overflow-x-auto p-5">
      <Table<Product>
        data={productsData}
        columns={columns}
        href="/app/products/create"
        manualPagination
      >
        <Table.Header className="p-6 w-full flex justify-between">
          <Table.SearchInput
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
          <Table.AddButton title="Producto" action={onClickAddButton} />
        </Table.Header>
        <Table.Content<Product> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination
            handleGoToPage={handleGoToPage}
            paginationInfo={{
              count: data.count,
              pageInfo: searchText
                ? {
                    ...pageInfoAlgolia,
                    currentPage: pageInfoAlgolia.currentPage + 1,
                  }
                : data.pageInfo,
            }}
            className="ml-auto"
          />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default ProductTable;
